import React, { useState, useEffect, useRef } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Container,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    useTheme,
    IconButton,
    useMediaQuery,
    styled,
    keyframes
} from '@mui/material'
import { CheckCircle, ArrowBackIos, ArrowForwardIos, Lock, LocalPhone } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../../context/LanguageContext'

// Theme Colors
const primaryColor = '#FF6D00'
const secondaryColor = '#FF9100'
const lightOrange = '#FFAB40'
const bgColor = '#FFF8F2'

// Animations
const floatAnimation = keyframes`
  0% { transform: translateY(0px) }
  50% { transform: translateY(-10px) }
  100% { transform: translateY(0px) }
`

const pulseAnimation = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 ${secondaryColor}80 }
  50% { transform: scale(1.05); box-shadow: 0 0 0 10px ${secondaryColor}00 }
  100% { transform: scale(1); box-shadow: 0 0 0 0 ${secondaryColor}00 }
`

const slideIn = keyframes`
  0% { opacity: 0; transform: translateY(20px) }
  100% { opacity: 1; transform: translateY(0) }
`

// Styled Components
const CardContainer = styled(Box)({
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: '680px',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    scrollSnapAlign: 'center'
})

const LuxuryCard = styled(Card)(({ popular, bgimage }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    border: popular ? 'none' : `1px solid ${primaryColor}20`,
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(255, 109, 0, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 16px 48px ${primaryColor}80`,
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${bgimage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: 0
    }
}))

const CardOverlay = styled(Box)(({ popular }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: popular
        ? `linear-gradient(135deg, ${primaryColor}30, ${secondaryColor}30)`
        : `linear-gradient(135deg, ${primaryColor}20, ${lightOrange}20)`,
    zIndex: 1
}))

const PopularBadge = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: -12,
    right: 20,
    backgroundColor: secondaryColor,
    color: '#000',
    padding: theme.spacing(0.75, 3),
    borderRadius: '20px',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    zIndex: 3,
    animation: `${pulseAnimation} 2s infinite ease-in-out`,
    transform: 'translateZ(0)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '20px',
        padding: '1px',
        background: `linear-gradient(135deg, ${lightOrange}, ${primaryColor})`,
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none'
    }
}))

const DiscountBadge = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 16,
    left: 16,
    backgroundColor: '#FF1744',
    color: 'white',
    padding: theme.spacing(0.5, 2),
    borderRadius: '12px',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    zIndex: 3,
    transform: 'translateZ(0)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '12px',
        padding: '1px',
        background: `linear-gradient(135deg, #FF5252, #D50000)`,
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none'
    }
}))

const FloatingParticles = styled('div')({
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 0,
    '&::before, &::after': {
        content: '""',
        position: 'absolute',
        background: `radial-gradient(${primaryColor}15 15%, transparent 60%)`,
        width: '400px',
        height: '400px',
        animation: `${floatAnimation} 8s infinite linear`,
    },
    '&::before': {
        top: '-20%',
        left: '-10%',
    },
    '&::after': {
        bottom: '-20%',
        right: '-10%',
        animationDelay: '-4s',
    }
})

// Price with slash component
const PriceWithSlash = ({ price, originalPrice, period }) => {
    return (
        <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: 'white',
                        opacity: 0.7,
                        textDecoration: 'line-through',
                        textDecorationThickness: '2px',
                        textDecorationColor: '#FF1744',
                        position: 'absolute',
                        top: '-20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {originalPrice}
                </Typography>
                <Typography
                    variant="h1"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                        lineHeight: 1,
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}
                >
                    {price}
                </Typography>
            </Box>
            <Typography
                component="span"
                variant="h6"
                sx={{
                    display: 'block',
                    mt: 1,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: 'white',
                    opacity: 0.9
                }}
            >
                {period}
            </Typography>
        </Box>
    )
}

// Background images for each package
const packageBackgrounds = {
    free: '/images/membership/free.png',
    gold: '/images/membership/gold.png',
    platinum: '/images/membership/platinum.png',
    titanium: '/images/membership/titanium.png',
}

const Membership = () => {
    const theme = useTheme()
    const { t } = useLanguage()
    const navigate = useNavigate()
    const isXS = useMediaQuery('(max-width:600px)')
    const isSM = useMediaQuery(theme.breakpoints.between(600, 900))
    const isMD = useMediaQuery(theme.breakpoints.between(900, 1200))
    const isLG = useMediaQuery(theme.breakpoints.up(1200))

    // Packages Data with translations
    const getPackages = () => ({
        free: {
            name: t('membership.free'),
            price: '0.00',
            period: t('membership.perYear'),
            features: Array(2).fill().map((_, i) => t(`membership.freeFeatures.${i}`)),
            images: [
                {
                    img: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: '美业导师课程导读'
                },
                {
                    img: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: '美业门店盈利36计'
                },
            ],
            videoUrl: 'https://www.youtube.com/watch?v=GKkEEIi7ROo&ab_channel=UnicornBeautyBusinessSchool',
            videoThumbnail: 'https://img.youtube.com/vi/GKkEEIi7ROo/maxresdefault.jpg',
            cta: t('membership.startFree'),
            registrationUrl: "https://beautyedu.app.clientclub.net/courses/offers/20e74193-4bf0-4b2a-b572-fa44ac61af45",
            popular: false,
            bgImage: packageBackgrounds.free,
            promotion: {
                title: "7天内升级特惠",
                items: [
                    "~ 橙美云课的【课程导读试听课】",
                    "~ 海老师【美业门店盈利36计试听课】",
                    "🎁 黄金会员🉑获得RM115现金折扣卷（线上即可使用）",
                    "🎁 白金会员🉑获得RM500现金折扣卷（指定线下课使用）",
                    "🉑获得RM345（RM115x3张）美业好友现金卷（只限使用于黄金会员费上）",
                    "🎁 黑金会员🉑获得",
                    "RM1000现金折扣卷（门店盈利36计全版）",
                    "RM1300现金折扣卷（指定线下课使用）",
                    "🉑获得RM690（RM115x6张）美业好友现金卷（只限使用于黄金会员费上）"
                ]
            }
        },
        gold: {
            name: t('membership.gold'),
            price: '',
            originalPrice: 'RM 480',
            period: t('membership.perYear'),
            features: [t('membership.goldFeatures.0')],
            images: [
                {
                    img: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '经营规划'
                },
                {
                    img: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '成交系统'
                },
                {
                    img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '组织管理'
                },
                {
                    img: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '人才机制'
                },
                {
                    img: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '美业目标管理'
                },
                {
                    img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '美业门店如何开发顾客'
                },
                {
                    img: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '美业门店拓客'
                },
                {
                    img: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '美业连锁创富'
                },
                {
                    img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '美业数据盈利'
                },
                {
                    img: 'https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '美业视觉视频课程'
                },
                {
                    img: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '经营与管理线上课'
                },
                {
                    img: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
                    title: '风教练直播课'
                }
            ],
            cta: t('membership.subscribeNow'),
            registrationUrl: "https://beautyedu.app.clientclub.net/courses/offers/a77e1cde-8b3d-44e6-a012-38d0c9320431",
            popular: true,
            bgImage: packageBackgrounds.gold,
            discount: true,
            discountText: 'Limited Time Offer',
            contactText: t('membership.callNow')
        },
        platinum: {
            name: t('membership.platinum'),
            price: 'RM 580',
            originalPrice: 'RM 680',
            period: t('membership.perYear'),
            features: [
                t('membership.platinumFeatures.0'),
                t('membership.platinumFeatures.1')
            ],
            images: [
                {
                    img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Team Meeting'
                },
                {
                    img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Collaboration'
                },
                {
                    img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Conference'
                },
                {
                    img: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Luxury Workspace'
                },
                {
                    img: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Executive Meeting'
                },
                {
                    img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Team Collaboration'
                },
                {
                    img: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Luxury Workspace'
                },
                {
                    img: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Executive Meeting'
                },
                {
                    img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Team Collaboration'
                }
            ],
            cta: t('membership.comingSoon'),
            registrationUrl: "#",
            popular: false,
            bgImage: packageBackgrounds.platinum,
            locked: true,
            releaseDate: "2024-01-01",
            discount: true,
            discountText: 'Early Bird Special'
        },
        titanium: {
            name: t('membership.titanium'),
            price: 'RM 1,480',
            period: t('membership.per3Years'),
            features: Array(5).fill().map((_, i) => t(`membership.titaniumFeatures.${i}`)),
            images: [
                {
                    img: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Executive Suite'
                },
                {
                    img: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'Premium Lounge'
                },
                {
                    img: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80',
                    title: 'VIP Boardroom'
                }
            ],
            cta: t('membership.contactUs'),
            registrationUrl: "#",
            popular: false,
            bgImage: packageBackgrounds.titanium,
            locked: true
        }
    })

    const [packages, setPackages] = useState(getPackages())
    const packagesArray = Object.values(packages)
    const [visibleCards, setVisibleCards] = useState(3)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [cardWidth, setCardWidth] = useState(0)
    const containerRef = useRef(null)
    const sliderRef = useRef(null)
    const cardGap = 24

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    // Update packages when language changes
    useEffect(() => {
        setPackages(getPackages())
    }, [t])

    useEffect(() => {
        const calculateLayout = () => {
            if (!containerRef.current) return
            const containerWidth = containerRef.current.offsetWidth
            let cardsToShow = 3

            if (isXS) cardsToShow = 1
            else if (isSM) cardsToShow = 2
            else if (isMD) cardsToShow = 3
            else if (isLG) cardsToShow = 4

            const effectiveWidth = containerWidth - (isXS ? 32 : 64)
            const calculatedWidth = (effectiveWidth - (cardGap * (cardsToShow - 1))) / cardsToShow

            setCardWidth(calculatedWidth)
            setVisibleCards(cardsToShow)
        }

        calculateLayout()
        window.addEventListener('resize', calculateLayout)
        return () => window.removeEventListener('resize', calculateLayout)
    }, [isXS, isSM, isMD, isLG])

    const canGoNext = currentSlide < packagesArray.length - visibleCards
    const canGoPrev = currentSlide > 0

    const handleNext = () => {
        if (canGoNext) {
            setCurrentSlide(prev => prev + 1)
        } else {
            setCurrentSlide(0) // Loop back to first slide
        }
    }

    const handlePrev = () => {
        if (canGoPrev) {
            setCurrentSlide(prev => prev - 1)
        } else {
            setCurrentSlide(packagesArray.length - visibleCards) // Go to last slide
        }
    }

    // Handle scroll snapping
    useEffect(() => {
        if (sliderRef.current) {
            sliderRef.current.scrollLeft = currentSlide * (cardWidth + cardGap)
        }
    }, [currentSlide, cardWidth])

    return (
        <Box sx={{
            backgroundColor: bgColor,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 248, 242, 0.9))',
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover'
        }}>
            <FloatingParticles />

            <Container maxWidth="xl" ref={containerRef} sx={{
                position: 'relative',
                zIndex: 2,
                px: { xs: 2, sm: 4 },
                py: 6
            }}>
                <Box textAlign="center" mb={10} sx={{ animation: `${slideIn} 0.8s ease-out` }}>
                    <Typography
                        variant="overline"
                        sx={{
                            letterSpacing: 6,
                            mb: 4,
                            fontSize: '1rem',
                            display: 'inline-block',
                            background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        {t('membership.exclusiveOffer')}
                    </Typography>
                    <Typography
                        variant="h1"
                        component="h2"
                        sx={{
                            mb: 6,
                            background: `linear-gradient(90deg, ${primaryColor}, ${lightOrange})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: isXS ? '2.5rem' : isSM ? '3rem' : '4rem',
                            lineHeight: 1.1
                        }}
                    >
                        {t('membership.premiumMembership')}
                    </Typography>
                </Box>

                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: '760px',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <IconButton
                        onClick={handlePrev}
                        disabled={!canGoPrev}
                        sx={{
                            position: 'absolute',
                            left: { xs: 8, sm: 16 },
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 6,
                            display: { xs: 'none', sm: 'flex' },
                            opacity: canGoPrev ? 0.9 : 0,
                            transition: 'all 0.3s ease',
                            '&:hover': { bgcolor: 'background.default', opacity: 1 }
                        }}
                    >
                        <ArrowBackIos />
                    </IconButton>

                    <Box
                        ref={sliderRef}
                        sx={{
                            display: 'flex',
                            gap: `${cardGap}px`,
                            width: '100%',
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            scrollBehavior: 'smooth',
                            py: 2,
                            px: { xs: 'calc(50% - (0.5 * (100% - 32px)))', sm: 0 },
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none'
                        }}
                    >
                        {packagesArray.map((pkg, index) => (
                            <CardContainer key={pkg.name} sx={{
                                width: { xs: `calc(100% - 32px)`, sm: cardWidth },
                                scrollSnapAlign: 'center',
                                position: 'relative',
                                filter: pkg.locked ? 'grayscale(0.7)' : 'none',
                                opacity: pkg.locked ? 0.85 : 1,
                                transition: 'all 0.3s ease',
                                mx: { xs: 1, sm: 0 },
                                '&:hover': {
                                    filter: pkg.locked ? 'grayscale(0.7)' : 'none',
                                    opacity: pkg.locked ? 0.85 : 1
                                }
                            }}>
                                {/* Locked Overlay */}
                                {pkg.locked && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(0,0,0,0.5)',
                                        borderRadius: '20px',
                                        backdropFilter: 'blur(2px)'
                                    }}>
                                        <Box sx={{
                                            textAlign: 'center',
                                            p: 3,
                                            color: 'white'
                                        }}>
                                            <Lock fontSize="large" sx={{ fontSize: '3rem', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                                {t('membership.comingSoon')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                {t('membership.availableFrom')} {new Date(pkg.releaseDate).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {pkg.popular && (
                                    <PopularBadge>
                                        {t('membership.mostPopular')}
                                    </PopularBadge>
                                )}
                                {pkg.discount && (
                                    <DiscountBadge>
                                        {pkg.discountText}
                                    </DiscountBadge>
                                )}
                                <LuxuryCard popular={pkg.popular} bgimage={pkg.bgImage}>
                                    <CardOverlay popular={pkg.popular} />
                                    <CardContent sx={{
                                        p: { xs: 3, sm: 4 },
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        position: 'relative',
                                        zIndex: 2,
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    }}>
                                        {/* Package Name */}
                                        <Box sx={{
                                            minHeight: 120,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2,
                                            p: 2,
                                        }}>
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    fontWeight: 800,
                                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                                    color: 'white',
                                                    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}
                                            >
                                                {pkg.name}
                                            </Typography>
                                        </Box>

                                        {/* Updated Pricing Section */}
                                        <Box sx={{
                                            mb: 3,
                                            background: 'rgba(0, 0, 0, 0.4)',
                                            borderRadius: '12px',
                                            p: 3,
                                            backdropFilter: 'blur(4px)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                        }}>
                                            {pkg.callForPrice ? (
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <LocalPhone fontSize="large" sx={{
                                                        fontSize: '3rem',
                                                        color: 'white',
                                                        mb: 2
                                                    }} />
                                                    <PriceWithSlash
                                                        price={pkg.price}
                                                        originalPrice={pkg.originalPrice}
                                                        period={pkg.period}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            opacity: 0.9
                                                        }}
                                                    >
                                                        {pkg.contactText}
                                                    </Typography>
                                                </Box>
                                            ) : pkg.originalPrice ? (
                                                <PriceWithSlash
                                                    price={pkg.price}
                                                    originalPrice={pkg.originalPrice}
                                                    period={pkg.period}
                                                />
                                            ) : (
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography
                                                        variant="h1"
                                                        sx={{
                                                            fontWeight: 900,
                                                            fontSize: { xs: '2rem', sm: '2.5rem' },
                                                            lineHeight: 1,
                                                            color: 'white',
                                                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                                        }}
                                                    >
                                                        {pkg.price}
                                                        <Typography
                                                            component="span"
                                                            variant="h6"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 1,
                                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                                color: 'white',
                                                                opacity: 0.9
                                                            }}
                                                        >
                                                            {pkg.period}
                                                        </Typography>
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        <Divider sx={{
                                            my: 3,
                                            borderWidth: 2,
                                            borderColor: 'rgba(255,255,255,0.3)'
                                        }} />

                                        {/* Features List */}
                                        <List dense sx={{
                                            flexGrow: 1,
                                            display: 'grid',
                                            gridTemplateRows: 'repeat(auto-fit, minmax(56px, 1fr))',
                                            mb: 3
                                        }}>
                                            {pkg.features.map((feature, i) => (
                                                <ListItem
                                                    key={i}
                                                    disableGutters
                                                    sx={{
                                                        minHeight: 56,
                                                        py: 0.5,
                                                        alignItems: 'center',
                                                        background: 'rgba(255,255,255,0.15)',
                                                        borderRadius: '8px',
                                                        mb: 1,
                                                        px: 2
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        <CheckCircle
                                                            fontSize="medium"
                                                            sx={{ color: 'white' }}
                                                        />
                                                    </ListItemIcon>
                                                    <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                                                        {feature}
                                                    </Typography>
                                                </ListItem>
                                            ))}
                                        </List>

                                        {/* CTA Button */}
                                        <Button
                                            fullWidth
                                            variant={pkg.popular ? 'contained' : 'outlined'}
                                            size="large"
                                            sx={{
                                                py: 2,
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                borderRadius: '12px',
                                                backgroundColor: pkg.popular ? secondaryColor : 'transparent',
                                                color: 'white',
                                                borderColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: pkg.popular ? lightOrange : `${primaryColor}40`,
                                                    boxShadow: `0 4px 12px ${primaryColor}50`
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                            onClick={() => pkg.callForPrice ? window.location.href = 'tel:+60123456789' : navigate(`/package/${pkg.name}`, { state: { package: pkg } })}
                                        >
                                            {pkg.cta}
                                        </Button>
                                    </CardContent>
                                </LuxuryCard>
                            </CardContainer>
                        ))}
                    </Box>

                    <IconButton
                        onClick={handleNext}
                        disabled={!canGoNext}
                        sx={{
                            position: 'absolute',
                            right: { xs: 8, sm: 16 },
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 6,
                            display: { xs: 'none', sm: 'flex' },
                            opacity: canGoNext ? 0.9 : 0,
                            transition: 'all 0.3s ease',
                            '&:hover': { bgcolor: 'background.default', opacity: 1 }
                        }}
                    >
                        <ArrowForwardIos />
                    </IconButton>
                </Box>
            </Container>
        </Box>
    )
}

export default Membership